import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { API_URL } from '../../config';
import { useCurrency } from '../../utils/currency';
import { useI18n } from '../../i18n/I18nContext';
import QuartierPanel from './QuartierPanel';
import 'leaflet/dist/leaflet.css';
import './CartePage.css';

// Fix default marker icon issue with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Icone quartier personnalisee
function createQuartierIcon(quartier, isSelected) {
  const color = isSelected ? '#BA7517' : getColorByPrice(quartier.prixM2Moyen);
  return L.divIcon({
    className: 'quartier-marker',
    html: `<div class="marker-pin" style="background:${color};border-color:${color}">
             <span>${quartier.nbAnnonces}</span>
           </div>
           <div class="marker-label">${quartier.nom}</div>`,
    iconSize: [40, 50],
    iconAnchor: [20, 50],
    popupAnchor: [0, -50]
  });
}

// Couleur selon prix/m2
function getColorByPrice(prixM2) {
  if (!prixM2 || prixM2 === 0) return '#999';
  if (prixM2 > 180000) return '#85563A'; // Cher = primary brown
  if (prixM2 > 130000) return '#BA7517'; // Moyen = accent gold
  return '#1D9E75'; // Accessible = secondary green
}

// Icones POI
function createPoiIcon(type) {
  const icons = {
    ecole: { emoji: '🎓', color: '#3B82F6' },
    universite: { emoji: '🎓', color: '#3B82F6' },
    mosquee: { emoji: '🕌', color: '#1D9E75' },
    transport: { emoji: '🚇', color: '#8B5CF6' },
    commerce: { emoji: '🛒', color: '#F59E0B' },
    sante: { emoji: '🏥', color: '#EF4444' },
    parc: { emoji: '🌳', color: '#10B981' }
  };
  const icon = icons[type] || { emoji: '📍', color: '#999' };
  return L.divIcon({
    className: 'poi-marker',
    html: `<div class="poi-pin" style="background:${icon.color}">${icon.emoji}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14]
  });
}

// Composant pour centrer la carte sur un quartier
function FlyToQuartier({ quartier }) {
  const map = useMap();
  useEffect(() => {
    if (quartier) {
      map.flyTo([parseFloat(quartier.latitude), parseFloat(quartier.longitude)], 14, {
        duration: 1
      });
    }
  }, [quartier, map]);
  return null;
}

function CartePage() {
  const { t } = useI18n();
  const [quartiers, setQuartiers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPois, setShowPois] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const { formatPrix } = useCurrency();

  // Charger les quartiers
  useEffect(() => {
    fetch(`${API_URL}/tools/carte/quartiers`)
      .then(r => r.json())
      .then(data => {
        setQuartiers(data.quartiers || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // Charger le detail du quartier selectionne
  useEffect(() => {
    if (!selected) {
      setDetail(null);
      return;
    }
    fetch(`${API_URL}/tools/carte/quartiers/${selected.id}`)
      .then(r => r.json())
      .then(data => {
        setDetail(data);
        setPanelOpen(true);
      })
      .catch(console.error);
  }, [selected]);

  const handleMarkerClick = (quartier) => {
    setSelected(quartier);
  };

  // Collecter tous les POIs du quartier selectionne
  const allPois = detail?.pois
    ? Object.values(detail.pois).flat()
    : [];

  return (
    <div className="carte-page">
      <div className="carte-header">
        <h1>{t.carte.title}</h1>
        <p>{t.carte.subtitle(quartiers.length)}</p>
      </div>

      <div className={`carte-layout ${panelOpen ? 'panel-open' : ''}`}>
        <div className="carte-map-container">
          {/* Legende */}
          <div className="carte-legende glass-card">
            <div className="legende-item">
              <span className="legende-dot" style={{background: '#1D9E75'}}></span>
              {t.carte.accessible}
            </div>
            <div className="legende-item">
              <span className="legende-dot" style={{background: '#BA7517'}}></span>
              {t.carte.moyen}
            </div>
            <div className="legende-item">
              <span className="legende-dot" style={{background: '#85563A'}}></span>
              {t.carte.premium}
            </div>
            <label className="legende-toggle">
              <input
                type="checkbox"
                checked={showPois}
                onChange={(e) => setShowPois(e.target.checked)}
              />
              {t.carte.poisLabel}
            </label>
          </div>

          {!loading && (
            <MapContainer
              center={[36.753, 3.058]}
              zoom={12}
              className="carte-leaflet"
              zoomControl={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              <FlyToQuartier quartier={selected} />

              {/* Marqueurs quartiers */}
              {quartiers.map(q => (
                <Marker
                  key={q.id}
                  position={[parseFloat(q.latitude), parseFloat(q.longitude)]}
                  icon={createQuartierIcon(q, selected?.id === q.id)}
                  eventHandlers={{
                    click: () => handleMarkerClick(q)
                  }}
                >
                  <Popup>
                    <div className="popup-content">
                      <strong>{q.nom}</strong>
                      <span className="popup-ar">{q.nom_ar}</span>
                      <span className="popup-ambiance">{q.ambiance}</span>
                      {q.nbAnnonces > 0 && (
                        <span className="popup-prix">{formatPrix(q.prixMoyen)}</span>
                      )}
                      <span className="popup-count">{q.nbAnnonces} annonce{q.nbAnnonces > 1 ? 's' : ''}</span>
                    </div>
                  </Popup>
                </Marker>
              ))}

              {/* Marqueurs POIs du quartier selectionne */}
              {showPois && selected && allPois.map(poi => (
                <Marker
                  key={`poi-${poi.id}`}
                  position={[parseFloat(poi.latitude), parseFloat(poi.longitude)]}
                  icon={createPoiIcon(poi.type)}
                >
                  <Popup>
                    <div className="popup-content">
                      <strong>{poi.nom}</strong>
                      <span className="popup-type">{poi.sous_type || poi.type}</span>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          )}
        </div>

        {/* Panel lateral */}
        {panelOpen && detail && (
          <QuartierPanel
            detail={detail}
            formatPrix={formatPrix}
            onClose={() => { setPanelOpen(false); setSelected(null); }}
          />
        )}
      </div>
    </div>
  );
}

export default CartePage;
